import { Ref } from 'vue';
import { IPagedList, defaultPagination } from '../core/models';
import { useCoreStore } from '../core/store';
import { useUiStore } from '..';
import { AppCoreEnums } from '@/core/shared/enums';
import { httpClient } from '@/core/axios/http-client';
import {INotificationState, MESSAGES_PAGE_SIZE, GROUP_USERS_PAGE_SIZE, GROUPS_PAGE_SIZE, INotificationGroup, INotificationGroupUser, INotificationMessage, ISaveNotificationGroupPayload, IMessageFilters, IUserLite, createEmptyMessageFilters,
} from './models';

export default (state: Ref<INotificationState>) => {
  const API_PREFIX = 'notifications';

  const coreStore = useCoreStore();

  const getAllGroups = (searchText = '', page = 1) => {
    const payload = {
      searchBy: searchText,
      filterItems: [],
      pagination: { ...defaultPagination, rowsPerPage: GROUPS_PAGE_SIZE, page },
    };

    return httpClient.post<void, IPagedList<INotificationGroup>>(`${API_PREFIX}/groups`, payload).then((response) => {
      state.value.adminGroups = page === 1 ? response.dataList : [...state.value.adminGroups, ...response.dataList];
      state.value.adminGroupsPagination = {
        page: response.pageNumber || page,
        rowsPerPage: GROUPS_PAGE_SIZE,
        rowsNumber: response.totalRecords,
      };

      const userIds: string[] = [];
      for (const group of response.dataList) userIds.push(...group.users.map((u) => u.userId));
      if (userIds.length) useUiStore().cacheUserAvatars(userIds);
    });
  };

  const reloadGroups = async () => {
    const lastPage = state.value.adminGroupsPagination.page;
    for (let page = 1; page <= lastPage; page++) await getAllGroups('', page);
  };

  const moveGroupToTop = (id: string) => {
    const index = state.value.adminGroups.findIndex((g) => g.id === id);
    if (index > 0) {
      const [group] = state.value.adminGroups.splice(index, 1);
      state.value.adminGroups.unshift(group);
    }
  };

  const getGroupMessages = (filters: IMessageFilters, page = 1, rowsPerPage?: number, sortBy?: string, descending = false) => {
    const idGroup = state.value.activeAdminGroup?.id;
    if (!idGroup) return Promise.resolve();

    const size = rowsPerPage ?? state.value.messagesPagination.rowsPerPage ?? MESSAGES_PAGE_SIZE;

    const filterItems = [];
    if (filters.dateFrom && filters.dateTo) {
      filterItems.push({
        fieldName: 'SentAt',
        filterType: AppCoreEnums.FilterItemType.Date,
        values: [filters.dateFrom, filters.dateTo],
      });
    }

    const payload = {
      searchBy: filters.searchText,
      filterItems,
      pagination: { ...defaultPagination, rowsPerPage: size, page, sortBy, descending },
    };

    return httpClient
      .post<void, IPagedList<INotificationMessage>>(`${API_PREFIX}/group-messages/${idGroup}`, payload)
      .then((response) => {
        state.value.messages = response.dataList;
        state.value.messagesPagination = {
          page: response.pageNumber || 1,
          rowsPerPage: size,
          rowsNumber: response.totalRecords,
          sortBy,
          descending,
        };
      });
  };

  const getCurrentUserMessages = (filters: IMessageFilters, page = 1, rowsPerPage?: number, sortBy?: string, descending = false) => {
    const size = rowsPerPage ?? state.value.messagesPagination.rowsPerPage ?? MESSAGES_PAGE_SIZE;

    const filterItems = [];
    if (filters.dateFrom && filters.dateTo) {
      filterItems.push({
        fieldName: 'SentAt',
        filterType: AppCoreEnums.FilterItemType.Date,
        values: [filters.dateFrom, filters.dateTo],
      });
    }

    const payload = {
      searchBy: filters.searchText,
      filterItems,
      pagination: { ...defaultPagination, rowsPerPage: size, page, sortBy, descending },
    };

    return httpClient
      .post<void, IPagedList<INotificationMessage>>(`${API_PREFIX}/my-messages`, payload)
      .then((response) => {
        state.value.messages = response.dataList;
        state.value.messagesPagination = {
          page: response.pageNumber || 1,
          rowsPerPage: size,
          rowsNumber: response.totalRecords,
          sortBy,
          descending,
        };
      });
  };

  const saveGroup = (payload: ISaveNotificationGroupPayload) => {
    return httpClient.post<void, string>(`${API_PREFIX}/save-group`, payload).then((response) => response);
  };

  const deleteGroup = (id: string) => {
    return httpClient.delete<void, boolean>(`${API_PREFIX}/delete-group/${id}`).then((response) => response);
  };

  const sendMessage = (payload: Pick<INotificationMessage, 'title' | 'message'>) => {
    const idGroup = state.value.activeAdminGroup?.id;
    return httpClient.post<void, string>(`${API_PREFIX}/send-message/${idGroup}`, payload).then((response) => response);
  };

  const markMessageAsSeen = (id: string) => {
    coreStore.useSilentApiCall = true;
    return httpClient
      .post<void, { idMessage: string; totalUnreadMessages: number }>(`${API_PREFIX}/seen-message`, { id })
      .then((response) => response)
      .finally(() => (coreStore.useSilentApiCall = false));
  };

  const getGroupUsers = (idGroup: string, filters: { searchText: string; page: number }) => {
    const payload = {
      searchBy: filters.searchText,
      filterItems: [],
      pagination: { ...defaultPagination, rowsPerPage: GROUP_USERS_PAGE_SIZE, page: filters.page },
    };

    return httpClient
      .post<void, IPagedList<INotificationGroupUser>>(`${API_PREFIX}/group-users/${idGroup}`, payload)
      .then((response) => response);
  };

  const getMessageSeenUsers = (idMessage: string, filters: { searchText: string; page: number }) => {
    const payload = {
      searchBy: filters.searchText,
      filterItems: [],
      pagination: { ...defaultPagination, rowsPerPage: GROUP_USERS_PAGE_SIZE, page: filters.page },
    };

    return httpClient
      .post<void, IPagedList<INotificationGroupUser>>(`${API_PREFIX}/message-seen-users/${idMessage}`, payload)
      .then((response) => response);
  };

  const loadUsersAutocomplete = () => {
    return httpClient.get<void, IUserLite[]>('users/all-users-lite').then((response) => response);
  };

  const initAdminView = async () => {
    await getAllGroups();

    if (state.value.adminGroups.length) {
      state.value.activeAdminGroup = state.value.adminGroups[0];
      await getGroupMessages(createEmptyMessageFilters(), 1);
    }
  };

  const initUserView = async () => {
    await getCurrentUserMessages(createEmptyMessageFilters(), 1);
  };

  return {
    getAllGroups,
    reloadGroups,
    moveGroupToTop,
    getGroupMessages,
    getCurrentUserMessages,
    saveGroup,
    deleteGroup,
    sendMessage,
    markMessageAsSeen,
    getGroupUsers,
    getMessageSeenUsers,
    loadUsersAutocomplete,
    initAdminView,
    initUserView,
  };
};
